import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Match, Campaign, Influencer } from '../../types';
import { UserType } from '../../constants/userTypes';
import { graphqlApi } from '../../services/graphql';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';
import { Spinner } from '../common/Spinner';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  BanknotesIcon,
  StarIcon,
  UserIcon
} from '@heroicons/react/24/outline';

interface MatchCardProps {
  match: Match;
  onUpdate?: (matchId: string, action: 'approve' | 'reject', feedback?: string) => void;
  userType?: UserType;
}

export const MatchCard: React.FC<MatchCardProps> = ({ match, onUpdate, userType }) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackAction, setFeedbackAction] = useState<'approve' | 'reject'>('approve');
  const [feedback, setFeedback] = useState('');
  
  // Use permission checks instead of userType prop
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canApproveMatches = hasPermission('matches:shortlist');
  const canRejectMatches = hasPermission('matches:reject');
  const canSendMessages = hasPermission('messages:send');

  useEffect(() => {
    fetchRelatedData();
  }, [match]);

  const fetchRelatedData = async () => {
    setLoading(true);
    try {
      // Always fetch campaign data
      if (match.campaignId) {
        const campaignResponse = await graphqlApi.getCampaign(match.campaignId);
        if (campaignResponse.success) {
          setCampaign(campaignResponse.data || null);
        }
      }

      // If host view, fetch influencer data
      if (userType === 'host' && match.influencerId) {
        const influencerResponse = await graphqlApi.getInfluencer(match.influencerId);
        if (influencerResponse.success) {
          setInfluencer(influencerResponse.data || null);
        }
      }
    } catch (error: any) {
      console.error('Error fetching related data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (action: 'approve' | 'reject') => {
    // Check permissions instead of userType
    const hasRequiredPermission = action === 'approve' ? canApproveMatches : canRejectMatches;
    
    if (hasRequiredPermission && onUpdate) {
      setFeedbackAction(action);
      setShowFeedbackModal(true);
    }
  };

  const submitAction = async () => {
    if (!onUpdate) return;

    setActionLoading(true);
    try {
      await onUpdate(match.matchId, feedbackAction, feedback);
      setShowFeedbackModal(false);
      setFeedback('');
    } catch (error) {
      toast.error(`Failed to ${feedbackAction} application`);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelAction = () => {
    setShowFeedbackModal(false);
    setFeedback('');
  };

  const handleMessage = async () => {
    // Show loading state
    setActionLoading(true);

    try {
      // Create conversation for this match automatically
      console.log('🔄 Creating conversation for match:', match.matchId);

      // Get campaign to determine hostId (campaign creator's userId)
      const campaignResponse = await graphqlApi.getCampaign(match.campaignId);
      console.log('campaignResponse', { campaignResponse });
      if (!campaignResponse.success || !campaignResponse.data) {
        throw new Error('Failed to get campaign data');
      }

      const campaign = campaignResponse.data;
      console.log('🔍 Campaign data:', campaign);
      const hostId = campaign.hostId; // Campaign creator is the host
      const influencerId = match.influencerId; // From the match record

      console.log('📋 Campaign fields:', { hostId: campaign.hostId, userId: campaign.userId, createdBy: campaign.createdBy });
      console.log('📋 Match data:', { influencerId, campaignId: match.campaignId });
      console.log('📋 Conversation participants:', { hostId, influencerId });

      if (!hostId) {
        throw new Error(`Campaign hostId is null. Campaign data: ${JSON.stringify(campaign, null, 2)}`);
      }

      // Check if conversation already exists for this match
      const existingConversation = await graphqlApi.getConversationByMatchId(match.matchId);

      let conversationResult;
      if (existingConversation.success && existingConversation.data) {
        console.log('✅ Conversation already exists for match:', match.matchId, existingConversation.data.conversationId);
        conversationResult = existingConversation;
      } else {
        console.log('🔄 Creating new conversation for match:', match.matchId);
        // Create new conversation with explicit hostId and influencerId
        conversationResult = await graphqlApi.createConversation(
          match.campaignId,
          match.matchId,
          hostId,
          influencerId
        );
      }

      if (conversationResult.success) {
        console.log('✅ Conversation ready:', conversationResult.data?.conversationId);
      } else {
        console.log('⚠️ Conversation creation failed or already exists:', conversationResult.error);
        // Continue anyway - might already exist or will be created when first message is sent
      }
    } catch (error) {
      console.log('⚠️ Error creating conversation:', error);
      // Continue anyway - conversation might already exist
    }

    // Navigate to messages page with conversation parameters
    navigate('/messages', {
      state: {
        campaignId: match.campaignId,
        matchId: match.matchId,
        userId: userType === 'host' ? match.influencerId : match.userId
      }
    });

    setActionLoading(false);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied':
        return 'bg-gray-100 text-gray-800';
      case 'shortlisted':
        return 'bg-indigo-100 text-indigo-800';
      case 'requested':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'applied':
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
      case 'shortlisted':
        return <StarIcon className="h-5 w-5 text-indigo-600" />;
      case 'requested':
        return <ClockIcon className="h-5 w-5 text-yellow-600" />;
      case 'accepted':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case 'expired':
        return <ClockIcon className="h-5 w-5 text-orange-600" />;
      case 'canceled':
        return <XCircleIcon className="h-5 w-5 text-gray-600" />;
      case 'completed':
        return <StarIcon className="h-5 w-5 text-blue-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex justify-center items-center h-32">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            {/* Campaign Name - Always show for context */}
            {campaign && (
              <div className="mb-3 pb-3 border-b border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Campaign</p>
                <h2 className="text-xl font-bold text-gray-900">{campaign.title}</h2>
                {campaign.category && (
                  <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    {campaign.category}
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center space-x-3 mb-2">
              <div className="flex items-center space-x-2">
                {getStatusIcon(match.status)}
                <h3 className="text-lg font-bold text-gray-900">
                  {canApproveMatches ? (
                    <>Application from {influencer?.name || 'Influencer'}</>
                  ) : (
                    <>Your Application</>
                  )}
                </h3>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(match.status)}`}>
                {match.status.charAt(0).toUpperCase() + match.status.slice(1)}
              </span>
            </div>

            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
              <span className="flex items-center">
                <UserIcon className="h-4 w-4 mr-1" />
                {canApproveMatches ? 'Influencer Application' : 'Your Application'}
              </span>
              <span className="flex items-center">
                <BanknotesIcon className="h-4 w-4 mr-1" />
                {match.proposedRate ? formatCurrency(match.proposedRate) : 'Rate not specified'}
              </span>
              <span className="flex items-center">
                <StarIcon className="h-4 w-4 mr-1" />
                {match.matchScore}% match
              </span>
            </div>

            {match.coverLetter && (
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-sm font-medium text-gray-900 mb-1">Cover Letter:</p>
                <p className="text-gray-600 text-sm">{match.coverLetter}</p>
              </div>
            )}

            {/* Additional application details */}
            {(match.whyGoodFit || match.portfolioItems?.length || match.estimatedDeliveryDays) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {match.whyGoodFit && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-600 mb-1">Why Good Fit:</p>
                    <p className="text-sm text-blue-800">{match.whyGoodFit}</p>
                  </div>
                )}
                {match.portfolioItems && match.portfolioItems.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-green-600 mb-1">Portfolio:</p>
                    <p className="text-sm text-green-800">{match.portfolioItems.length} items</p>
                  </div>
                )}
                {match.estimatedDeliveryDays && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-purple-600 mb-1">Delivery Time:</p>
                    <p className="text-sm text-purple-800">{match.estimatedDeliveryDays} days</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Applied</span>
            <span className="text-sm font-medium text-gray-900">{formatDate(match.appliedAt)}</span>
          </div>

          {match.respondedAt && (
            <div className="flex flex-col">
              <span className="text-xs text-gray-500">Responded</span>
              <span className="text-sm font-medium text-gray-900">{formatDate(match.respondedAt)}</span>
            </div>
          )}

          {match.completedAt && (
            <div className="flex flex-col">
              <span className="text-xs text-gray-500">Completed</span>
              <span className="text-sm font-medium text-gray-900">{formatDate(match.completedAt)}</span>
            </div>
          )}
        </div>

        {/* Status-specific information */}
        {match.status === 'applied' && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <ClockIcon className="h-4 w-4 text-gray-600 mr-2" />
              <span className="text-sm text-gray-800">
                {canApproveMatches
                  ? 'New application — review and shortlist if suitable'
                  : 'Your application has been submitted and is under review'
                }
              </span>
            </div>
          </div>
        )}

        {match.status === 'shortlisted' && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <StarIcon className="h-4 w-4 text-indigo-600 mr-2" />
              <span className="text-sm text-indigo-800">
                {canApproveMatches
                  ? 'Shortlisted — send a collaboration request when ready'
                  : 'You have been shortlisted as a candidate'
                }
              </span>
            </div>
          </div>
        )}

        {match.status === 'requested' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <ClockIcon className="h-4 w-4 text-yellow-600 mr-2" />
              <span className="text-sm text-yellow-800">
                {canApproveMatches
                  ? 'Collaboration request sent — waiting for influencer response'
                  : 'You have received a collaboration request — please accept or decline'
                }
              </span>
            </div>
          </div>
        )}

        {match.status === 'accepted' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-sm text-green-800">
                {canApproveMatches
                  ? 'Collaboration accepted. You can now start working together'
                  : 'You accepted the collaboration. Campaign is in progress'
                }
              </span>
            </div>
            {match.hostFeedback && (
              <div className="mt-2 pt-2 border-t border-green-200">
                <p className="text-xs font-medium text-green-600">Host Feedback:</p>
                <p className="text-sm text-green-800">{match.hostFeedback}</p>
              </div>
            )}
          </div>
        )}

        {match.status === 'rejected' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <XCircleIcon className="h-4 w-4 text-red-600 mr-2" />
              <span className="text-sm text-red-800">
                {canApproveMatches
                  ? 'Collaboration was declined by the influencer'
                  : 'You declined this collaboration request'
                }
              </span>
            </div>
            {match.rejectionReason && (
              <div className="mt-2 pt-2 border-t border-red-200">
                <p className="text-xs font-medium text-red-600">Reason:</p>
                <p className="text-sm text-red-800">{match.rejectionReason}</p>
              </div>
            )}
          </div>
        )}

        {match.status === 'expired' && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <ClockIcon className="h-4 w-4 text-orange-600 mr-2" />
              <span className="text-sm text-orange-800">
                This request expired due to no response within the time limit
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center">
              <ClockIcon className="h-4 w-4 mr-1" />
              Applied {formatDate(match.appliedAt)}
            </span>
          </div>

          <div className="flex space-x-2">
            {match.status === 'accepted' && canSendMessages && (
              <button
                onClick={handleMessage}
                disabled={actionLoading || permissionsLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors inline-flex items-center disabled:opacity-50">
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                    Message
                  </>
                )}
              </button>
            )}

            {/* Host actions: shortlist (applied), request (shortlisted) */}
            {match.status === 'applied' && canApproveMatches && onUpdate && (
              <button
                onClick={() => handleAction('approve')}
                disabled={actionLoading || permissionsLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Shortlist
              </button>
            )}

            {match.status === 'shortlisted' && canApproveMatches && onUpdate && (
              <button
                onClick={() => handleAction('approve')}
                disabled={actionLoading || permissionsLoading}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Send Request
              </button>
            )}

            {/* Influencer actions: accept/reject (requested) */}
            {match.status === 'requested' && !canApproveMatches && onUpdate && (
              <div className="flex space-x-2">
                <button
                  onClick={() => handleAction('approve')}
                  disabled={actionLoading || permissionsLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Accept
                </button>
                <button
                  onClick={() => handleAction('reject')}
                  disabled={actionLoading || permissionsLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Decline
                </button>
              </div>
            )}

            {/* Host action: cancel request */}
            {match.status === 'requested' && canApproveMatches && onUpdate && (
              <button
                onClick={() => handleAction('reject')}
                disabled={actionLoading || permissionsLoading}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel Request
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900">
                {feedbackAction === 'approve' ? 'Approve Application' : 'Reject Application'}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-3">
                  {feedbackAction === 'approve'
                    ? 'Provide feedback for the approved influencer:'
                    : 'Please provide a reason for rejection:'}
                </p>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={feedbackAction === 'approve'
                    ? 'Great application! Looking forward to working together...'
                    : 'Thank you for your interest, however...'}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                />
              </div>
              <div className="flex items-center px-4 py-3 space-x-2">
                <button
                  onClick={submitAction}
                  disabled={actionLoading}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
                    feedbackAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {actionLoading ? <Spinner /> : `${feedbackAction === 'approve' ? 'Approve' : 'Reject'}`}
                </button>
                <button
                  onClick={cancelAction}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
